import {
  Loading
} from "./anacapri-chunk.S5GFW45J.js";
import {
  newDialogManager
} from "./anacapri-chunk.LWFEFJQ2.js";
import {
  Button
} from "./anacapri-chunk.RKG3IP5A.js";
import {
  to
} from "./anacapri-chunk.5OKHRCVN.js";
import {
  Rt
} from "./anacapri-chunk.5KELFBS4.js";
import {
  base_default,
  e,
  n,
  r,
  r2,
  token_default
} from "./anacapri-chunk.3P6V43X6.js";
import {
  AnacapriElement
} from "./anacapri-chunk.3HZGRT44.js";
import {
  customElement_default
} from "./anacapri-chunk.YC5RXRZP.js";
import {
  AnacapriEvent
} from "./anacapri-chunk.3ETXNLOE.js";
import {
  Logger_default
} from "./anacapri-chunk.J7V767XI.js";
import {
  i
} from "./anacapri-chunk.TOMZZDRF.js";
import {
  ke
} from "./anacapri-chunk.7OQQIROZ.js";
import {
  D
} from "./anacapri-chunk.37PM4VTN.js";
import {
  __decorateClass
} from "./anacapri-chunk.AHXTNXK6.js";

// src/components/dialog/dialog.css
var styles = i`:host {
  --ana-dialog-border-radius: var(--ao-border-radius-s);
}

/** DIALOG CONTAINER **/

.dialog-container {
  position: fixed;
  top: 0;
  left: 0;
  padding: 0;
  background: transparent;
  border: none;
  outline: none;
  z-index: 100;
}

.dialog-container.nonmodal {
  top: auto;
  right: 0;
  bottom: 0;
  left: auto;
}

.dialog-container:not(.nonmodal) {
  width: 100vw;
  height: 100vh;
  transform-origin: center;
  overflow-y: auto;
}

.dialog-container:not(.nonmodal)[open] {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.dialog-backdrop {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--ao-color-blue-dark);
  opacity: 0;
  transition: all 0.2s ease;
}

.dialog-backdrop.active {
  display: initial;
  opacity: 0.66;
}

/** DIALOG BOX **/

.dialog {
  /**
    If \`--ana-dialog-width\` is defined we use that, otherwise we default
    to using column sizing.

    \`--ana-dialog-width-column\` is expected to be 1 - 12 to match Foundation grid columns.
    This calc finds the percentage of the max width the dialog should take up.
  **/
  width: var(
    --ana-dialog-width,
    calc(var(--ao-width-max) * calc(var(--ana-dialog-width-column, 8) / 12))
  );
  font-size: 1rem;
  text-align: left;
  opacity: 0;
  visibility: hidden;
  background-color: white;
  box-shadow: var(--ao-shadow-l);
  border: none;
  border-radius: calc(var(--ana-dialog-border-radius) + 1px)
    calc(var(--ana-dialog-border-radius) + 1px) var(--ana-dialog-border-radius)
    var(--ana-dialog-border-radius);
  z-index: 1;
}

.dialog-container:not(.nonmodal) .dialog {
  max-width: calc(100% - 2rem);
  margin: 6rem 1rem;
}

@media screen and (max-width: 620px) {
  .dialog {
    max-width: calc(100% - 1rem);
    margin: 6rem 0.5rem;
  }
}

/** DIALOG HEADER **/

.dialog-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  color: var(--ao-color-attention);
  background-color: var(--ao-color-blue);
  box-shadow: 0 4px 0 var(--ao-color-attention);
  border-top-left-radius: var(--ana-dialog-border-radius);
  border-top-right-radius: var(--ana-dialog-border-radius);
}

.dialog-heading-container {
  display: inline-block;
  flex: 10;
}

.dialog-heading {
  margin: 0;
}

.dialog-close {
  flex: 0 1 2rem;
}

/** DIALOG CONTENT **/

.dialog-container:not(.nonmodal) .dialog-content {
  display: block;
  margin: 3rem 1rem;
}

.dialog-container.nonmodal .dialog-content {
  display: block;
  padding: 1rem;
}

@media print {
  .dialog-backdrop.active {
    display: none;
  }

  .dialog-container.dialog-container {
    position: relative;
    overflow: visible;
    height: auto;
  }

  .dialog.dialog.dialog {
    border: none;
    box-shadow: none;
    width: 100%;
    margin: 0;
  }

  .dialog-header {
    background: none;
    border: none;
    box-shadow: none;
  }

  .dialog-close {
    display: none;
  }
}
`;
var dialog_default = styles;

// src/components/dialog/dialog.ts
var Dialog = class extends AnacapriElement {
  constructor() {
    super();
    /**
     * Current index in dialog stack.
     */
    this.currentPosition = 0;
    this.isActive = false;
    this.backdropCancel = false;
    this.disableEscapeCancel = false;
    this.heightEstimate = "500px";
    this.hideClose = false;
    this.manualControl = false;
    this.open = false;
    this.width = 8;
    this.nonmodal = false;
    this.manager = newDialogManager;
    this.type = "modal" /* Modal */;
    this.disableAnimation = false;
    this.animationDuration = 200;
    this.handleKeyboardInput = this.handleKeyboardInput.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleAutofocus = this.handleAutofocus.bind(this);
  }
  /**
   * Scroll to the top of the dialog. Useful on submit if you need the user to notice error banners.
   * @param options {ScrollToOptions} - @see https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollTo
   */
  scrollToTop(options = {}) {
    if (typeof this.dialogContainer?.scrollTo === "function") {
      this.dialogContainer?.scrollTo({ ...options, top: 0 });
    }
  }
  async animateDialog(eventName, keyframes, options = {
    duration: this.animationDuration,
    easing: "ease",
    fill: "forwards"
  }, onFinish = () => {
  }) {
    if (typeof this.dialog?.animate === "function" && !this.disableAnimation) {
      if (this.animation) {
        if (this.dialog.checkVisibility && this.dialog.checkVisibility()) {
          this.animation.commitStyles();
        }
        this.animation.cancel();
      }
      this.animation = this.dialog?.animate(keyframes, options);
      const event = new AnacapriEvent(eventName);
      this.dispatchEvent(event);
      try {
        await this.animation.finished;
        this.animation.commitStyles();
        this.animation = null;
      } catch (error) {
        Logger_default.logInfo(error);
      } finally {
        await onFinish();
        const finishEvent = new AnacapriEvent(`${eventName}-finished`);
        this.dispatchEvent(finishEvent);
      }
    } else {
      const event = new AnacapriEvent(eventName);
      this.dispatchEvent(event);
      await onFinish();
      const finishEvent = new AnacapriEvent(`${eventName}-finished`);
      this.dispatchEvent(finishEvent);
      if (this.dialog) {
        const finalStyles = keyframes[keyframes.length - 1];
        for (const prop in finalStyles) {
          const key = prop;
          this.dialog.style[key] = finalStyles[prop];
        }
      }
    }
  }
  /**
   * Show the dialog
   */
  async show() {
    this.open = true;
  }
  /**
   * Hide the dialog
   */
  async hide() {
    this.open = false;
  }
  /**
   * Update the position of the dialog in the stack of dialogs.
   * This will animate the dialog forward or back based on the new
   * position relative to the current position.
   * @param newPosition - index in the array of open dialogs, with 0 being the front-most.
   * @param zIndex - position of dialog along the z axis.
   * @returns - promise that resolves when animation is complete.
   */
  updatePositionInDialogStack(newPosition, zIndex = 200) {
    this.isActive = newDialogManager.isActiveDialog(this);
    this.dialogBackdrop.style.zIndex = zIndex.toString();
    if (this.dialogContainer) {
      this.dialogContainer.style.zIndex = zIndex.toString();
    }
    for (const widget of this.widgets) {
      widget.style.display = this.isActive ? "contents" : "none";
    }
    return this.animateDialog(
      "move",
      [
        {
          transform: `translate(${0 * this.currentPosition}px, ${-45 * this.currentPosition}px) scale(${1 - 0.05 * this.currentPosition})`
        },
        {
          transform: `translate(${0 * newPosition}px, ${-45 * newPosition}px) scale(${1 - 0.05 * newPosition})`
        }
      ],
      {
        delay: newPosition * 50,
        duration: 150,
        easing: "ease",
        fill: "forwards"
      },
      () => {
        this.currentPosition = newPosition;
      }
    );
  }
  openDialog() {
    this.focusedElementOnClose = document.activeElement;
    newDialogManager.openDialog(this);
    this.isActive = newDialogManager.isActiveDialog(this);
    if (typeof this.dialogContainer?.show === "function") {
      this.dialogContainer.show();
    }
    this.scrollToTop();
    this.animateDialog(
      "open",
      [
        {
          transform: `scale(.95)`,
          opacity: 0,
          visibility: "hidden"
        },
        {
          transform: `scale(1)`,
          opacity: 1,
          visibility: "visible"
        }
      ],
      void 0,
      this.handleAutofocus
    );
  }
  async closeDialog() {
    newDialogManager.closeDialog(this);
    this.isActive = newDialogManager.isActiveDialog(this);
    await this.animateDialog(
      "close",
      [
        {
          transform: `scale(1)`,
          opacity: 1,
          visibility: "visible"
        },
        {
          transform: `scale(.95)`,
          opacity: 0,
          visibility: "hidden"
        }
      ],
      void 0,
      () => {
        if (typeof this.dialogContainer?.close === "function") {
          this.dialogContainer.close();
        }
      }
    );
    if (this.focusedElementOnClose) {
      this.focusedElementOnClose.focus();
    }
  }
  handleAutofocus() {
    const autofocusField = this.querySelector("[autofocus]");
    if (autofocusField) {
      autofocusField.focus();
    } else {
      this.dialog?.focus();
    }
  }
  handleCancel(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const cancelEvent = new AnacapriEvent("cancel");
    this.dispatchEvent(cancelEvent);
    if (!this.manualControl) {
      this.open = false;
    }
  }
  handleKeyboardInput(event) {
    if (newDialogManager.isActiveDialog(this) && event.key === "Escape") {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!this.disableEscapeCancel) {
        this.handleCancel(event);
      }
    }
  }
  handleBackdropClick(event) {
    if (this.backdropCancel && event.target === this.dialogContainer) {
      this.handleCancel(event);
    }
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this.handleKeyboardInput);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.handleKeyboardInput);
    this.closeDialog();
  }
  firstUpdated() {
    if (this.open) {
      this.openDialog();
    }
    if (this.nonmodal) {
      this.type = "nonmodal" /* NonModal */;
    }
    if (this.dialog && this.width) {
      this.dialog.style.setProperty("--ana-dialog-width-column", this.width.toString());
    }
  }
  willUpdate(changedProperties) {
    const previousOpenValue = changedProperties.get("open");
    if (previousOpenValue !== void 0 && previousOpenValue !== this.open) {
      if (this.open) {
        this.openDialog();
      } else {
        this.closeDialog();
      }
    }
    if (this.dialog) {
      this.dialog.style.setProperty("--ana-dialog-width-column", this.width.toString());
    }
    if (changedProperties.has("nonmodal") && !changedProperties.has("type")) {
      this.type = this.nonmodal ? "nonmodal" /* NonModal */ : this.type;
    }
  }
  /* eslint-disable lit-a11y/click-events-have-key-events */
  /* eslint-disable lit/binding-positions */
  /* eslint-disable lit/no-invalid-html */
  render() {
    return ke`
      <div class=${Rt({
      "dialog-backdrop": true,
      active: this.isActive
    })}></div>
      <dialog
        class=${Rt({
      "dialog-container": true,
      nonmodal: this.type === "nonmodal" /* NonModal */
    })}
        role=${to(this.type === "alert" /* Alert */ ? "alertdialog" : void 0)}
        aria-label=${to(this.heading)}
        aria-describedby=${to(this.description ? "dialog-description" : void 0)}
        part="base"
        @click=${this.handleBackdropClick}
      >
        <div
          class="dialog"
          part="dialog"
          .inert=${!this.isActive && this.type !== "nonmodal" /* NonModal */}
        >
          <header class="dialog-header" part="header">
            <slot name="heading" class="dialog-heading-container">
              <h2 class="dialog-heading">${this.heading}</h2>
            </slot>
            ${!this.hideClose ? ke`<${Button.tag} 
              class="dialog-close" 
              .variant=${"close"} 
              @click="${this.handleCancel}"></${Button.tag}>` : D}
          </header>
          ${this.description ? ke`<span id="dialog-description" class="sr-only">${this.description}</span>` : D}
          <slot part="content" class="dialog-content">
            <div
              style="height: ${this.heightEstimate}; display: flex; justify-content: center; align-items: center;"
            >
              <${Loading.tag}></${Loading.tag}>
            </div>
          </slot>
        </div>
        <slot
        .inert=${!this.open}
        name="widget"></slot>
      </dialog>
    `;
  }
};
Dialog.styles = [base_default, token_default, dialog_default];
__decorateClass([
  e(".dialog-backdrop")
], Dialog.prototype, "dialogBackdrop", 2);
__decorateClass([
  e("dialog")
], Dialog.prototype, "dialogContainer", 2);
__decorateClass([
  e(".dialog")
], Dialog.prototype, "dialog", 2);
__decorateClass([
  r2('slot[name="widget"]')
], Dialog.prototype, "widgets", 2);
__decorateClass([
  r()
], Dialog.prototype, "isActive", 2);
__decorateClass([
  n({ attribute: "backdrop-cancel", type: Boolean })
], Dialog.prototype, "backdropCancel", 2);
__decorateClass([
  n({ attribute: "disable-escape-cancel", type: Boolean })
], Dialog.prototype, "disableEscapeCancel", 2);
__decorateClass([
  n()
], Dialog.prototype, "heading", 2);
__decorateClass([
  n({ attribute: "height-estimate" })
], Dialog.prototype, "heightEstimate", 2);
__decorateClass([
  n({ attribute: "hide-close", type: Boolean })
], Dialog.prototype, "hideClose", 2);
__decorateClass([
  n({ attribute: "manual-control", type: Boolean })
], Dialog.prototype, "manualControl", 2);
__decorateClass([
  n({ type: Boolean, reflect: true })
], Dialog.prototype, "open", 2);
__decorateClass([
  n({ type: Number })
], Dialog.prototype, "width", 2);
__decorateClass([
  n({ type: Boolean })
], Dialog.prototype, "nonmodal", 2);
__decorateClass([
  n({ attribute: false })
], Dialog.prototype, "manager", 2);
__decorateClass([
  n({ attribute: "z-index", type: Number })
], Dialog.prototype, "zIndex", 2);
__decorateClass([
  n()
], Dialog.prototype, "type", 2);
__decorateClass([
  n()
], Dialog.prototype, "description", 2);
__decorateClass([
  n({ attribute: "disable-animation", type: Boolean })
], Dialog.prototype, "disableAnimation", 2);
__decorateClass([
  n({ attribute: "animation-duration", type: Number })
], Dialog.prototype, "animationDuration", 2);
Dialog = __decorateClass([
  customElement_default("ana-dialog")
], Dialog);

export {
  Dialog
};
